// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { BookText, Plus } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Select, Input, Pagination, DataTable } from '@/components/ui'
import type { Column } from '@/components/ui/DataTable'
import { useJournalEntries } from '@/hooks/useAccounting'
import { useProperties } from '@/hooks/useProperties'
import { useSelectedPropertyStore } from '@/stores'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { JournalEntry } from '@/types'
import { formatBaht } from '@/lib/money'

type StatusFilter = '' | 'draft' | 'posted' | 'voided'

export function JournalEntryListPage() {
  const { t } = useTranslation('accounting')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const formatDate = useDateFormatter()

  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<StatusFilter>('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)

  const { data, isLoading, isFetching, error } = useJournalEntries(page, {
    status: status || undefined,
    from: from || undefined,
    to: to || undefined,
    property_id: selectedPropertyId ?? undefined,
  })

  const { data: propertiesData } = useProperties()
  const properties = propertiesData?.data || []

  const entries = data?.data || []

  const propertyOptions = [
    { value: '', label: t('filters.all_properties') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const statusTabs: { value: StatusFilter; label: string }[] = [
    { value: '', label: tc('labels.all') },
    { value: 'draft', label: t('statuses.draft') },
    { value: 'posted', label: t('statuses.posted') },
    { value: 'voided', label: t('statuses.voided') },
  ]

  const getStatusBadge = (entry: JournalEntry) => {
    switch (entry.status) {
      case 'posted':
        return <Badge variant="success">{t('statuses.posted')}</Badge>
      case 'voided':
        return <Badge variant="ghost">{t('statuses.voided')}</Badge>
      default:
        return <Badge variant="warning">{t('statuses.draft')}</Badge>
    }
  }

  const columns: Column<JournalEntry>[] = [
    {
      key: 'entry_number',
      header: t('labels.entry_number'),
      render: (e) => <span className="font-mono font-medium truncate max-w-[100px] block">{e.entry_number}</span>,
    },
    {
      key: 'entry_date',
      header: t('labels.entry_date'),
      render: (e) => formatDate(e.entry_date),
    },
    {
      key: 'description',
      header: t('labels.description'),
      render: (e) => (
        <span className="truncate max-w-xs block">{e.description}</span>
      ),
      hideBelow: 'md',
    },
    {
      key: 'status',
      header: t('labels.status'),
      render: (e) => getStatusBadge(e),
    },
    {
      key: 'total_debit',
      header: t('labels.total_debit'),
      render: (e) => (
        <span className="font-medium tabular-nums">{formatBaht(e.total_debit, { locale: 'en', decimals: 2 })}</span>
      ),
    },
    {
      key: 'property',
      header: t('labels.property'),
      render: (e) => e.property?.name || '—',
      hideBelow: 'md',
    },
  ]

  const activeJournalFilterCount = [status, selectedPropertyId, from, to].filter(Boolean).length

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('journal_entries.title') }]} />

      <PageHeader
        title={t('journal_entries.title')}
        description={t('journal_entries.description')}
        isRefreshing={isFetching && !isLoading}
        // Property scope sits top-right (TC-MUI-A5 titleAction, mobile-only; the
        // desktop picker stays in the filter card below) — consistent with the
        // Financial Statements page and the list pages. New Entry is the primary.
        titleAction={
          <Select
            data-testid="page-property-picker-mobile"
            aria-label={tc('labels.select_property')}
            size="sm"
            value={selectedPropertyId ? String(selectedPropertyId) : ''}
            onChange={(e) => {
              setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)
              setPage(1)
            }}
            options={propertyOptions}
          />
        }
        primaryAction={{
          key: 'new',
          label: t('journal_entries.new_entry'),
          icon: Plus,
          to: '/accounting/journal-entries/new',
        }}
      />

      {/* Filters */}
      <Card>
        {/* Mobile-only toggle */}
        <button
          type="button"
          className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium"
          aria-expanded={mobileFiltersOpen}
          aria-controls="journal-filters-body"
          onClick={() => setMobileFiltersOpen((v) => !v)}
        >
          <span>
            {tc('filters.toggle')}
            {activeJournalFilterCount > 0 && (
              <Badge variant="count" size="sm" className="ml-2">{activeJournalFilterCount}</Badge>
            )}
          </span>
          <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
        </button>

        <div
          id="journal-filters-body"
          className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
        >
          <div className="flex gap-1 self-center overflow-x-auto">
            {statusTabs.map((tab) => (
              <Button
                key={tab.value}
                size="sm"
                variant={status === tab.value ? 'primary' : 'ghost'}
                className="flex-shrink-0 whitespace-nowrap"
                onClick={() => {
                  setStatus(tab.value)
                  setPage(1)
                }}
              >
                {tab.label}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full sm:flex-1 sm:min-w-[280px]">
            <Input
              label={t('filters.date_from')}
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value)
                setPage(1)
              }}
            />
            <Input
              label={t('filters.date_to')}
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value)
                setPage(1)
              }}
            />
          </div>
        </div>
      </Card>

      {/* Content */}
      {isLoading ? (
        <Card>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            title={t('messages.error_loading')}
            description={t('messages.error_description')}
            action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
          />
        </Card>
      ) : entries.length === 0 ? (
        <Card>
          <EmptyState
            icon={<BookText className="w-12 h-12" />}
            title={t('journal_entries.empty_title')}
            description={t('journal_entries.empty_description')}
            action={
              <Button
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => navigate('/accounting/journal-entries/new')}
              >
                {t('journal_entries.new_entry')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            data={entries}
            keyExtractor={(e) => e.id}
            onRowClick={(e) => navigate(`/accounting/journal-entries/${e.id}`)}
          />
        </Card>
      )}

      {data?.meta && data.meta.total_count > 0 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={data.meta.current_page}
            totalPages={data.meta.total_pages}
            totalItems={data.meta.total_count}
            pageSize={data.meta.per_page}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  )
}
